import { useEffect, useState } from 'react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Box,
  Button,
  CollectionPreferences,
  Header,
  Select,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
  Toggle,
} from '@amzn/awsui-components-console';
import { SelectProps } from '@amzn/awsui-components-console/select/interfaces';
import {
  FetchedLaunchData,
  TraslatedDocumentStatus,
} from '../../../interfaces';
import {
  ExportLaunchTasksCommand,
  ExportLaunchTasksCommandOutput,
  LaunchTask,
  WorkStatus,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { EmptyState } from '../../ui/EmptyState';
import { WorkStatusBadge } from './WorkStatusBadge';
import {
  convertToPT,
  displayBuildStatus,
  displayPersonAssigned,
  getListOfPackages,
  mapBuildRequestStatusToReadableString,
} from '../utils';
import { searchableTextForColumn } from '../searchableColumns';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { DeleteModal } from './DeleteModal';
import { useAuthState } from '../../../authentication/context';
import { downloadCsv } from '../../../downloadCsv';
import { callApi } from '../../../client';
import { matchesTaskFilters } from '../../../utils/statusFilters';
import { compositeVenueKey } from '../../../utils/venueDisplay';
import { Link } from '@amzn/awsui-components-console';
import { CreateGroupModal } from '../../../GroupPages/CreateGroupModal';
import { AddTasksToGroupModal } from '../../../GroupPages/AddTasksToGroupModal';
import { AskFusionAssistantButton } from '../../../fusionAssistant/AskFusionAssistantButton';

export const ConferenceListTable = ({
  data,
  loading,
  venueDisplayName,
  onRefresh,
}: FetchedLaunchData) => {
  //data = mockLaunchTasks
  const { path } = useRouteMatch();
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState<LaunchTask[]>([]);
  // Single-select workStatus "view": mutually-exclusive lifecycle buckets. Default = any status other
  // than GROUPED/DELETED/RESOLVED. Archived and Unassigned are ORTHOGONAL toggles (below) that
  // AND-compose with the selected view (tcx-core-platforms-903).
  const [statusView, setStatusView] = useState<
    'default' | 'grouped' | 'deleted' | 'resolved'
  >('default');
  const [showEmptyWriter, setShowEmptyWriter] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Quick "Assigned to me" filter (tcx-core-platforms V2373634323): orthogonal, AND-composed toggle
  // like Archived/Unassigned, matching rows whose writer alias equals the current user's alias.
  const [showMineOnly, setShowMineOnly] = useState(false);
  const [createGroupModalVisible, setCreateGroupModalVisible] = useState(false);
  const [addToGroupModalVisible, setAddToGroupModalVisible] = useState(false);
  const SERVICES_COLUMNS = 'conferenceColumns';
  const { token, isAuthenticated, userAlias } = useAuthState();
  const CONFERENCES_COLUMNS = 'conferencesColumns';
  const CONFERENCES_PAGE_SIZE = 'conferencesPageSize';
  const COLUMN_MIN_WIDTH = 200;
  const DEFAULT_VISIBLE_COLUMNS = [
    'name',
    'launchId',
    'conference',
    'venue',
    'workStatus',
    'writerAssignment',
    'productName',
    'lastTimeToBuild',
    'packageBuildInfoList',
    'resolveVenueTime',
    'modifiedAt',
    'lastBuildDate',
    'lastBuildStatus',
  ];
  const [preferences, setPreferences] = useState({
    pageSize: 20,
    wrapLines: true,
    visibleContent: DEFAULT_VISIBLE_COLUMNS,
  });
  // Set initial visible columns from localStorage if it exists
  useEffect(() => {
    const storedColumns = localStorage.getItem(CONFERENCES_COLUMNS);
    const storedPageSize = localStorage.getItem(CONFERENCES_PAGE_SIZE);

    if (storedColumns || storedPageSize) {
      setPreferences((prev) => ({
        ...prev,
        ...(storedColumns && { visibleContent: storedColumns.split(',') }),
        ...(storedPageSize && { pageSize: parseInt(storedPageSize) }),
      }));
    }
  }, []);
  // Save column preferences to localStorage
  useEffect(() => {
    localStorage.setItem(
      CONFERENCES_COLUMNS,
      preferences.visibleContent?.toString() ?? ''
    );
  }, [preferences.visibleContent]);

  // Save page size preference to localStorage
  useEffect(() => {
    localStorage.setItem(
      CONFERENCES_PAGE_SIZE,
      preferences.pageSize?.toString() ?? ''
    );
  }, [preferences.pageSize]);

  const conferenceColumns = [
    {
      id: 'name',
      header: 'Launch name',
      cell: (s: LaunchTask) =>
        s.name ? (
          s.launchId ? (
            <Link href={`#${path}/view-${s.launchId}`}>{s.name}</Link>
          ) : (
            s.name
          )
        ) : (
          '-'
        ),
      sortingField: 'name',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'launchId',
      header: 'Launch ID',
      cell: (s: LaunchTask) => (s.launchId ? s.launchId : '-'),
      sortingField: 'launchId',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'conference',
      header: 'Conference',
      cell: (s: LaunchTask) => s.conference || '-',
      sortingField: 'conference',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'venue',
      header: 'Venue',
      // Show the coordinator's canonical name when set, else the raw venue (display-only; sorting/
      // filtering/identity stay on the raw venue). tcx-core-platforms-903 D2.
      cell: (s: LaunchTask) =>
        venueDisplayName
          ? venueDisplayName(s.conference, s.venue)
          : s.venue || '-',
      sortingField: 'venue',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'workStatus',
      header: 'Work status',
      cell: (s: LaunchTask) =>
        s.workStatus ? (
          <WorkStatusBadge status={s.workStatus} showPrefix={false} />
        ) : (
          '-'
        ),
      sortingField: 'workStatus',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'writerAssignment',
      header: 'Assignee',
      cell: (s: LaunchTask) =>
        displayPersonAssigned(s.writerAssignment?.alias) || '-',
      sortingField: 'writerAssignment',
      minWidth: COLUMN_MIN_WIDTH,
      sortingComparator: (s1: LaunchTask, s2: LaunchTask) => {
        const alias1 = s1.writerAssignment?.alias ?? '';
        const alias2 = s2.writerAssignment?.alias ?? '';

        if (!alias1 && !alias2) {
          return 0;
        }
        if (!alias1) {
          return 1;
        }
        if (!alias2) {
          return -1;
        }
        return alias1.localeCompare(alias2);
      },
    },
    {
      id: 'productName',
      header: 'Product name',
      cell: (s: LaunchTask) => s.productName || '-',
      sortingField: 'productName',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'launchDate',
      header: 'Launch date',
      cell: (s: LaunchTask) => convertToPT(s.launchDate),
      sortingField: 'launchDate', //PT
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'packageBuildInfoList',
      header: 'Package list',
      cell: (s: LaunchTask) => getListOfPackages(s.packageBuildInfoList) || '-',
      sortingField: 'packageBuildInfoList',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'lastTimeToBuild',
      header: 'Final build deadline (ELTB)',
      cell: (s: LaunchTask) => convertToPT(s.lastTimeToBuild, true),
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'resolveVenueTime',
      header: 'Resolve venue time',
      cell: (s: LaunchTask) => convertToPT(s.resolveVenueTime, true),
      sortingField: 'resolveVenueTime',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'modifiedAt',
      header: 'Last updated date',
      cell: (s: LaunchTask) => convertToPT(s.modifiedAt),
      sortingField: 'modifiedAt',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'lastBuildDate',
      header: 'Last build date',
      cell: (s: LaunchTask) => convertToPT(s.packageBuildInfoModifiedAt),
      sortingField: 'lastBuildDate',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'lastBuildStatus',
      header: 'Last build status',
      cell: (s: LaunchTask) =>
        s.buildRequestStatus
          ? displayBuildStatus(
              mapBuildRequestStatusToReadableString(s.buildRequestStatus)
            )
          : '-',
      sortingField: 'lastBuildStatus',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'dueDate',
      header: 'Due date',
      cell: (s: LaunchTask) => convertToPT(s.dueDate, false),
      sortingField: 'dueDate',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'status',
      header: 'Launch status',
      cell: (s: LaunchTask) => s.status || '-',
      sortingField: 'status',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'documentationStatus',
      header: 'Documentation status',
      cell: (s: LaunchTask) =>
        s.documentationStatus
          ? TraslatedDocumentStatus[s.documentationStatus]
          : '-',
      sortingField: 'documentationStatus',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'type',
      header: 'Launch type',
      cell: (s: LaunchTask) => (s.type ? s.type : '-'),
      sortingField: 'type',
      minWidth: COLUMN_MIN_WIDTH,
    },
  ];

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const textMatch = (inputText: string, verifyingText: string) => {
    return inputText.toLowerCase().includes(verifyingText.toLowerCase());
  };

  // Free-text search matches the SAME text each column renders, so a query
  // hits exactly what the user sees. Keyed by column id and used by the
  // filteringFunction over the currently-visible columns; keep in sync with the
  // conferenceColumns cells when a column's rendering changes.
  //  The useCollection React hook takes original items and a configuration and returns filtered, sorted and paginated content, according to your configuration.
  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(data || [], {
    filtering: {
      empty: (
        // TODO: Update this empty state to use an Alert component
        <EmptyState
          title='The list of services couldn’t be loaded'
          subtitle='Try refreshing your browser. If that doesn’t work, our team is most likely working on resolving it.'
          action={
            <Button onClick={() => location.reload()}>Refresh browser </Button>
          }
        />
      ),
      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='No services matched the specified filter'
          action={
            <Button onClick={() => actions.setFiltering('')}>
              Clear filter
            </Button>
          }
        />
      ),
      filteringFunction: (launchData, filteringText) => {
        if (!launchData) {
          return false;
        }
        // Three orthogonal, AND-joined hidden dimensions (tcx-core-platforms-903): the single-select
        // statusView, the Archived toggle, and the Unassigned toggle. Delegated to the pure
        // matchesTaskFilters helper (unit-tested in utils/statusFilters.test.ts).
        if (
          !matchesTaskFilters({
            workStatus: launchData.workStatus,
            archived: launchData.archived,
            hasAssignee: Boolean(launchData.writerAssignment),
            view: statusView,
            showArchived,
            showUnassigned: showEmptyWriter,
            assigneeAlias: launchData.writerAssignment?.alias,
            currentUserAlias: userAlias,
            showMineOnly,
          })
        ) {
          return false;
        }

        // Free-text search runs over the columns the user currently has
        // visible (preferences.visibleContent), matching the same text each
        // column renders — so a query hits exactly what the user sees, and
        // hiding a column removes it from the search.
        return preferences.visibleContent.some((colId) =>
          textMatch(
            searchableTextForColumn(colId, launchData, venueDisplayName),
            filteringText
          )
        );
      },
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {
      defaultState: {
        sortingColumn: conferenceColumns[0],
        isDescending: false,
      },
    },
    selection: {},
  });

  const exportAll = async () => {
    if (token) {
      try {
        const response: ExportLaunchTasksCommandOutput = await callApi(
          new ExportLaunchTasksCommand({}),
          token
        );

        if (response.$metadata.httpStatusCode == 200) {
          const text = new TextDecoder().decode(response.output);
          downloadCsv(`FLC-export-${+new Date()}`, text);
        } else {
          console.error(
            'Unexpected response status code:',
            response.$metadata.httpStatusCode
          );
        }
      } catch (err: any) {
        console.log('Error with ExportLaunchTasks API', err);
      }
    } else {
      alert(
        'Your session has expired. Please refresh the page to re-authenticate.'
      );
    }
  };

  const INVALID_STATUSES_FOR_GROUPING: WorkStatus[] = [
    WorkStatus.GROUPED,
    WorkStatus.DELETED,
    WorkStatus.RESOLVED,
    WorkStatus.SHIPPED,
  ];

  const canAddToGroup = (): boolean => {
    if (selectedItems.length === 0) return false;
    const hasInvalidStatus = selectedItems.some((t) =>
      INVALID_STATUSES_FOR_GROUPING.includes(t.workStatus)
    );
    if (hasInvalidStatus) return false;
    // All selected tasks must share the same (conference, venue) — the group's scope is taken from
    // selectedItems[0]. Keying on venue alone would wrongly allow a multi-select spanning two
    // conferences that happen to have a same-named venue (tcx-core-platforms-903 F4). The composite
    // key is built via the shared compositeVenueKey helper so the delimiter/field order can't drift
    // from the other call sites (finding on CR-301380769).
    const scopes = new Set(
      selectedItems.map((t) => compositeVenueKey(t.conference, t.venue))
    );
    if (scopes.size > 1) return false;
    return true;
  };

  // Single-select workStatus view (mutually exclusive). Archived + Unassigned are separate orthogonal
  // toggles rendered alongside it. tcx-core-platforms-903.
  const STATUS_VIEW_OPTIONS: SelectProps.Option[] = [
    { label: 'Active tasks', value: 'default' },
    { label: 'Grouped tasks', value: 'grouped' },
    { label: 'Deleted tasks', value: 'deleted' },
    { label: 'Resolved tasks', value: 'resolved' },
  ];
  const selectedStatusViewOption: SelectProps.Option =
    STATUS_VIEW_OPTIONS.find((o) => o.value === statusView) ??
    STATUS_VIEW_OPTIONS[0];

  return (
    <Box margin={{ top: 'l', bottom: 'xxl' }}>
      <Table
        {...collectionProps}
        resizableColumns
        onSelectionChange={({ detail }) => {
          setSelectedItems(detail.selectedItems);
        }}
        selectedItems={selectedItems}
        columnDefinitions={conferenceColumns}
        items={items}
        loadingText='Retrieving launch data'
        loading={loading}
        selectionType='single'
        trackBy='launchId'
        visibleColumns={preferences.visibleContent}
        stickyHeader={true}
        header={
          <Header
            actions={
              onRefresh && (
                <Button
                  iconName='refresh'
                  onClick={onRefresh}
                  data-test-id='refresh-launch-tasks-button'
                >
                  Refresh
                </Button>
              )
            }
            counter={
              data && filteredItemsCount
                ? `(${filteredItemsCount}/${data.length})`
                : `(${items.length})`
            }
          >
            Conference tasks
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        wrapLines
        filter={
          // Search sits on its own full-width line (it was too compressed when
          // inline with the other controls). Below it, the remaining controls
          // share a line that wraps by screen width — status filter + toggles
          // left-aligned, mutating action buttons pushed right (marginLeft:auto)
          // to visually separate filters from actions.
          <SpaceBetween size='m'>
            <TextFilter
              {...filterProps}
              filteringPlaceholder='Search tasks by visible fields, like launch name, ID, package, assignee, product, or status'
            />
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Select
                  selectedOption={selectedStatusViewOption}
                  onChange={({ detail }) =>
                    setStatusView(
                      (detail.selectedOption.value as
                        | 'default'
                        | 'grouped'
                        | 'deleted'
                        | 'resolved') ?? 'default'
                    )
                  }
                  options={STATUS_VIEW_OPTIONS}
                  data-test-id='status-view-select'
                />
                <Toggle
                  checked={showArchived}
                  onChange={({ detail }) => setShowArchived(detail.checked)}
                  data-test-id='archived-toggle'
                >
                  Archived
                </Toggle>
                <Toggle
                  checked={showEmptyWriter}
                  onChange={({ detail }) => setShowEmptyWriter(detail.checked)}
                  data-test-id='unassigned-toggle'
                >
                  Unassigned
                </Toggle>
                <Toggle
                  checked={showMineOnly}
                  onChange={({ detail }) => setShowMineOnly(detail.checked)}
                  disabled={!userAlias}
                  data-test-id='assigned-to-me-toggle'
                >
                  Assigned to me
                </Toggle>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '8px',
                  marginLeft: 'auto',
                }}
              >
                <Button
                  disabled={selectedItems.length === 0 || !canAddToGroup()}
                  onClick={() => setAddToGroupModalVisible(true)}
                  data-test-id='add-to-group-button'
                >
                  Add to group
                </Button>
                <Button
                  //disabling for 1st release
                  //disabled={true}
                  disabled={
                    selectedItems.length === 0 ||
                    selectedItems[0].workStatus === WorkStatus.DELETED
                  }
                  onClick={openModal}
                >
                  Delete
                </Button>

                {showModal && (
                  <DeleteModal
                    launchId={selectedItems[0].launchId}
                    onClose={closeModal}
                  />
                )}
                <Button
                  //disabling for 1st release
                  disabled={selectedItems.length === 0}
                  variant='primary'
                  href={`#${path}/view-${
                    selectedItems.length > 0 ? selectedItems[0].launchId : '0'
                  }`}
                >
                  View details
                </Button>
                <Button onClick={exportAll}>Export all</Button>
                <Button
                  variant='primary'
                  onClick={() => setCreateGroupModalVisible(true)}
                  data-test-id='create-group-button'
                >
                  Create group
                </Button>
                <AskFusionAssistantButton />
              </div>
            </div>
          </SpaceBetween>
        }
        preferences={
          <CollectionPreferences
            onConfirm={({ detail }) =>
              setPreferences({
                ...preferences,
                pageSize: detail.pageSize ?? 20,
                // Cloudscape types visibleContent as optional; coalesce a
                // possible undefined to the default so the free-text search's
                // preferences.visibleContent.some(...) can never throw.
                visibleContent:
                  (detail.visibleContent as string[] | undefined) ??
                  DEFAULT_VISIBLE_COLUMNS,
              })
            }
            preferences={preferences}
            visibleContentPreference={{
              title: 'Select visible content',
              options: [
                {
                  label: 'Columns: ',
                  options: [
                    { id: 'name', label: 'Launch name' },
                    { id: 'launchId', label: 'Launch ID' },
                    { id: 'conference', label: 'Conference' },
                    { id: 'venue', label: 'Venue' },
                    { id: 'workStatus', label: 'Work status' },
                    { id: 'writerAssignment', label: 'Assignee' },
                    { id: 'productName', label: 'Product name' },
                    { id: 'launchDate', label: 'Launch date' },
                    { id: 'packageBuildInfoList', label: 'Package list' },
                    {
                      id: 'lastTimeToBuild',
                      label: 'Final build deadline (ELTB)',
                    },
                    {
                      id: 'resolveVenueTime',
                      label: 'Resolve venue time',
                    },
                    { id: 'modifiedAt', label: 'Last updated date' },
                    { id: 'lastBuildDate', label: 'Last build date' },
                    { id: 'lastBuildStatus', label: 'Last build status' },
                    { id: 'dueDate', label: 'Due date' },
                    { id: 'status', label: 'Launch status' },
                    {
                      id: 'documentationStatus',
                      label: 'Documentation status',
                    },
                    { id: 'type', label: 'Launch type' },
                  ],
                },
              ],
            }}
            pageSizePreference={{
              title: 'Select page size',
              options: [
                { value: 20, label: '20 launches' },
                { value: 50, label: '50 launches' },
              ],
            }}
            cancelLabel='Cancel'
            confirmLabel='Confirm'
            title='Preferences'
          />
        }
      />

      <CreateGroupModal
        visible={createGroupModalVisible}
        onDismiss={() => setCreateGroupModalVisible(false)}
        onSuccess={(groupId) => {
          setCreateGroupModalVisible(false);
          history.push(`/groups/view-${groupId}`);
        }}
      />

      {addToGroupModalVisible && selectedItems.length > 0 && (
        <AddTasksToGroupModal
          visible={addToGroupModalVisible}
          conference={selectedItems[0].conference ?? ''}
          venue={selectedItems[0].venue ?? ''}
          preSelectedTaskIds={selectedItems.map((t) => t.launchId)}
          onDismiss={() => setAddToGroupModalVisible(false)}
          onSuccess={(groupId) => {
            setAddToGroupModalVisible(false);
            history.push(`/groups/view-${groupId}`);
          }}
          onSwitchToCreate={() => {
            setAddToGroupModalVisible(false);
            setCreateGroupModalVisible(true);
          }}
        />
      )}
    </Box>
  );
};
